import { TutorialStep, PageTutorialConfig } from '../types';

// Header steps for admin context
export const adminHeaderSteps: TutorialStep[] = [
  {
    target: '[data-onboarding="sidebar-trigger"]',
    title: "Menu administration",
    message: "Accédez à toutes les sections d'administration : utilisateurs, contenus, paramètres...",
    position: "bottom-right"
  },
  {
    target: '[data-onboarding="home-button"]',
    title: "Dashboard Admin",
    message: "Retournez au tableau de bord pour une vue d'ensemble",
    position: "bottom"
  },
  {
    target: '[data-onboarding="back-button"]',
    title: "Retour",
    message: "Revenez à la page précédente",
    position: "bottom"
  },
  {
    target: '[data-onboarding="role-badge"]',
    title: "Mode Administrateur",
    message: "Vous avez accès à toutes les fonctionnalités d'administration",
    position: "bottom"
  },
  {
    target: '[data-onboarding="logout-button"]',
    title: "Déconnexion",
    message: "Déconnectez-vous de l'espace administrateur",
    position: "bottom-left"
  },
  {
    target: '[data-onboarding="help-button"]',
    title: "Aide",
    message: "Cliquez ici à tout moment pour revoir ce tutoriel",
    position: "bottom-left"
  },
  // Sidebar admin sections
  {
    target: '[data-onboarding="sidebar-admin-general"]',
    title: "Général",
    message: "Tableau de bord, missions et gestion des utilisateurs",
    position: "bottom-right"
  },
  {
    target: '[data-onboarding="sidebar-admin-utilisateurs"]',
    title: "Utilisateurs",
    message: "Gérez les clients, prestataires et administrateurs",
    position: "bottom-right"
  },
  {
    target: '[data-onboarding="sidebar-admin-contenus"]',
    title: "Contenus",
    message: "Gérez les annonces, FAQ, lexique et documents",
    position: "bottom-right"
  },
  {
    target: '[data-onboarding="sidebar-admin-commercial"]',
    title: "Commercial",
    message: "Tarifs, RBA et gestion des profils partenaires",
    position: "bottom-right"
  },
  {
    target: '[data-onboarding="sidebar-admin-analyse"]',
    title: "Analyse",
    message: "Statistiques, analytics et catégorisation des missions",
    position: "bottom-right"
  },
  {
    target: '[data-onboarding="sidebar-admin-technique"]',
    title: "Technique",
    message: "Configuration des voix IA, embedding et mode test",
    position: "bottom-right"
  },
  {
    target: '[data-onboarding="sidebar-admin-parametres"]',
    title: "Paramètres",
    message: "Calendrier, mot de passe et installation de l'app",
    position: "bottom-right"
  }
];

// Page-specific tutorials for admin pages
export const adminPageConfigs: PageTutorialConfig[] = [
  // Dashboard
  {
    routePattern: "/admin/dashboard",
    exactMatch: true,
    steps: [
      {
        target: '[data-onboarding="admin-stats-cards"]',
        title: "Statistiques globales",
        message: "Vue d'ensemble des missions, utilisateurs et activité de la plateforme.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="admin-recent-activity"]',
        title: "Activité récente",
        message: "Suivez les dernières actions et événements en temps réel.",
        position: "bottom"
      }
    ]
  },
  // Missions
  {
    routePattern: "/admin/missions",
    exactMatch: true,
    tabDataAttribute: "data-missions-active-tab",
    steps: (activeTab) => {
      const baseSteps: TutorialStep[] = [
        {
          target: '[data-onboarding="missions-search"]',
          title: "Recherche",
          message: "Recherchez une mission par ID, description ou client.",
          position: "bottom"
        },
        {
          target: '[data-onboarding="missions-filters"]',
          title: "Filtres",
          message: "Filtrez par statut, date, type de mission ou prestataire.",
          position: "bottom"
        }
      ];
      
      if (activeTab === "moderation") {
        return [
          ...baseSteps,
          {
            target: '[data-onboarding="moderation-queue"]',
            title: "File de modération",
            message: "Validez ou rejetez les missions signalées ou à risque.",
            position: "bottom"
          }
        ];
      }
      
      return [
        ...baseSteps,
        {
          target: '[data-onboarding="missions-list"]',
          title: "Liste des missions",
          message: "Cliquez sur une mission pour voir ses détails et son historique.",
          position: "bottom"
        }
      ];
    }
  },
  // Clients
  {
    routePattern: "/admin/clients",
    exactMatch: true,
    steps: [
      {
        target: '[data-onboarding="clients-search"]',
        title: "Recherche client",
        message: "Trouvez un client par nom, email ou téléphone.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="clients-list"]',
        title: "Liste des clients",
        message: "Consultez et gérez les profils clients.",
        position: "bottom"
      }
    ]
  },
  // Prestataires
  {
    routePattern: "/admin/prestataires",
    exactMatch: true,
    tabDataAttribute: "data-prestataires-active-tab",
    steps: (activeTab) => {
      if (activeTab === "verification") {
        return [
          {
            target: '[data-onboarding="presta-verification-queue"]',
            title: "File de vérification",
            message: "Validez les documents et informations des nouveaux prestataires.",
            position: "bottom"
          },
          {
            target: '[data-onboarding="presta-documents"]',
            title: "Documents",
            message: "Vérifiez les attestations d'assurance, SIRET et certifications.",
            position: "bottom"
          }
        ];
      }
      
      return [
        {
          target: '[data-onboarding="presta-search"]',
          title: "Recherche prestataire",
          message: "Trouvez un prestataire par nom, entreprise ou compétence.",
          position: "bottom"
        },
        {
          target: '[data-onboarding="presta-list"]',
          title: "Liste des prestataires",
          message: "Gérez les profils, statuts et documents des prestataires.",
          position: "bottom"
        }
      ];
    }
  },
  // Embedding Test - with tab support
  {
    routePattern: "/admin/embedding",
    exactMatch: true,
    tabDataAttribute: "data-embedding-active-tab",
    steps: (activeTab) => {
      if (activeTab === "competences") {
        return [
          {
            target: '[data-onboarding="embedding-tabs"]',
            title: "1️⃣ Onglet Compétences",
            message: "Vous êtes sur l'onglet Compétences. Ici vous voyez les prestataires et leurs scores de correspondance après un Match.",
            position: "bottom"
          },
          {
            target: '[data-onboarding="embedding-min-score"]',
            title: "2️⃣ Filtrer par score minimum",
            message: "Entrez un pourcentage minimum pour ne voir que les compétences avec un score de correspondance élevé.",
            position: "bottom"
          },
          {
            target: '[data-onboarding="embedding-sort-score"]',
            title: "3️⃣ Trier par score",
            message: "Cliquez pour trier les compétences par score de correspondance (croissant ou décroissant).",
            position: "bottom"
          },
          {
            target: '[data-onboarding="embedding-competence-card"]',
            title: "4️⃣ Sélectionner des compétences",
            message: "Cochez les compétences/prestataires que vous souhaitez contacter. Le bouton 'Créer propositions' apparaîtra en bas.",
            position: "bottom"
          }
        ];
      }
      
      // Default: missions tab
      return [
        {
          target: '[data-onboarding="embedding-categorize-btn"]',
          title: "1️⃣ Catégoriser les missions",
          message: "Lance l'IA pour catégoriser automatiquement toutes les missions (famille et sous-famille) selon leur description.",
          position: "bottom-left"
        },
        {
          target: '[data-onboarding="embedding-generate-btn"]',
          title: "2️⃣ Générer les embeddings",
          message: "Génère les vecteurs d'embedding pour toutes les missions et compétences. Les pastilles vertes ✓ Emb indiquent qu'un embedding existe.",
          position: "bottom-left"
        },
        {
          target: '[data-onboarding="embedding-tabs"]',
          title: "3️⃣ Navigation par onglets",
          message: "Vous êtes sur l'onglet Missions. Cliquez sur Compétences pour voir/sélectionner les prestataires après le matching.",
          position: "bottom"
        },
        {
          target: '[data-onboarding="embedding-mission-card"]',
          title: "4️⃣ Sélectionner une mission",
          message: "Cochez une mission pour la sélectionner. Les boutons 'Match' et 'Régénérer' apparaîtront. Cliquez sur la flèche pour voir les détails.",
          position: "bottom"
        },
        {
          target: '[data-onboarding="embedding-match-btn"]',
          title: "5️⃣ Match IA",
          message: "Lance le matching IA pour trouver les prestataires les plus compatibles avec cette mission. Les résultats apparaîtront dans l'onglet Compétences.",
          position: "bottom"
        },
        {
          target: '[data-onboarding="embedding-regen-btn"]',
          title: "6️⃣ Régénérer l'embedding",
          message: "Régénère le vecteur d'embedding de cette mission. Utile si la description a été modifiée pour améliorer le matching.",
          position: "bottom"
        }
      ];
    }
  },
  // FAQ Admin
  {
    routePattern: "/admin/faq",
    exactMatch: true,
    steps: [
      {
        target: '[data-onboarding="faq-add"]',
        title: "Ajouter une FAQ",
        message: "Créez une nouvelle question/réponse pour la FAQ.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="faq-categories"]',
        title: "Catégories",
        message: "Organisez les FAQ par catégories pour une meilleure navigation.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="faq-list"]',
        title: "Liste des FAQ",
        message: "Modifiez ou supprimez les questions existantes.",
        position: "bottom"
      }
    ]
  },
  // Lexique Admin
  {
    routePattern: "/admin/lexique",
    exactMatch: true,
    steps: [
      {
        target: '[data-onboarding="lexique-add"]',
        title: "Ajouter un terme",
        message: "Ajoutez un nouveau terme ou acronyme au lexique.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="lexique-list"]',
        title: "Liste des termes",
        message: "Gérez les définitions existantes.",
        position: "bottom"
      }
    ]
  },
  // Annonces Promo
  {
    routePattern: "/admin/annonces-promo",
    exactMatch: true,
    steps: [
      {
        target: '[data-onboarding="annonces-add"]',
        title: "Créer une annonce",
        message: "Créez une nouvelle annonce promotionnelle ou informative.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="annonces-planning"]',
        title: "Planification",
        message: "Programmez les dates de début et fin de diffusion.",
        position: "bottom"
      }
    ]
  },
  // Tarifs
  {
    routePattern: "/admin/tarifs",
    exactMatch: true,
    steps: [
      {
        target: '[data-onboarding="tarifs-grid"]',
        title: "Grille tarifaire",
        message: "Modifiez les tarifs par type de service et zone géographique.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="tarifs-majoration"]',
        title: "Majorations",
        message: "Configurez les majorations week-end, jours fériés et urgences.",
        position: "bottom"
      }
    ]
  },
  // Audio
  {
    routePattern: "/admin/audio",
    exactMatch: true,
    steps: [
      {
        target: '[data-onboarding="audio-generate"]',
        title: "Générer un audio",
        message: "Créez un fichier audio à partir d'un texte.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="audio-list"]',
        title: "Fichiers générés",
        message: "Gérez et téléchargez les fichiers audio créés.",
        position: "bottom"
      }
    ]
  },
  // Stats
  {
    routePattern: "/admin/stats",
    exactMatch: true,
    steps: [
      {
        target: '[data-onboarding="stats-period"]',
        title: "Période",
        message: "Sélectionnez la période d'analyse (jour, semaine, mois, année).",
        position: "bottom"
      },
      {
        target: '[data-onboarding="stats-charts"]',
        title: "Graphiques",
        message: "Analysez les tendances et performances de la plateforme.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="stats-export"]',
        title: "Export",
        message: "Exportez les données en CSV ou PDF pour vos rapports.",
        position: "bottom"
      }
    ]
  },
  // Legal Documents
  {
    routePattern: "/admin/legal-documents",
    exactMatch: true,
    steps: [
      {
        target: '[data-onboarding="legal-docs-list"]',
        title: "Documents légaux",
        message: "Gérez les CGU, CGV, politique de confidentialité et autres documents.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="legal-docs-version"]',
        title: "Versions",
        message: "Créez de nouvelles versions et suivez les acceptations utilisateurs.",
        position: "bottom"
      }
    ]
  },
  // Test Mode
  {
    routePattern: "/admin/test-mode",
    exactMatch: true,
    steps: [
      {
        target: '[data-onboarding="test-mode-toggle"]',
        title: "Mode test",
        message: "Activez/désactivez le mode test pour simuler des scénarios.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="test-mode-scenarios"]',
        title: "Scénarios",
        message: "Choisissez un scénario prédéfini ou créez le vôtre.",
        position: "bottom"
      }
    ]
  },
  // Calendrier Admin
  {
    routePattern: "/admin/calendrier",
    exactMatch: true,
    steps: [
      {
        target: '[data-onboarding="admin-calendar-view"]',
        title: "Vue calendrier",
        message: "Visualisez toutes les missions planifiées sur la plateforme.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="admin-calendar-filters"]',
        title: "Filtres",
        message: "Filtrez par prestataire, type de mission ou zone.",
        position: "bottom"
      }
    ]
  },
  // System Tools
  {
    routePattern: "/admin/system-tools",
    exactMatch: true,
    steps: [
      {
        target: '[data-onboarding="system-refresh-btn"]',
        title: "1️⃣ Actualiser les données",
        message: "Recharge les statistiques de l'historique unifié depuis la base de données.",
        position: "bottom-left"
      },
      {
        target: '[data-onboarding="system-stat-total"]',
        title: "2️⃣ Total événements",
        message: "Nombre total d'entrées dans historique_unifie, qui centralise tous les événements de la plateforme.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="system-stat-pending"]',
        title: "3️⃣ En attente d'embedding",
        message: "Événements sans embedding. Utilisez 'Générer les embeddings' pour les traiter.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="system-migration-batch"]',
        title: "4️⃣ Batch de migration",
        message: "Nombre d'enregistrements à migrer par lot. Recommandé: 100-200.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="system-migration-source"]',
        title: "5️⃣ Source des données",
        message: "Choisissez quelle table migrer: missions, propositions, conversations IA ou toutes.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="system-migration-btn"]',
        title: "6️⃣ Lancer la migration",
        message: "Importe les données historiques vers historique_unifie. À exécuter une seule fois.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="system-embedding-batch"]',
        title: "7️⃣ Batch d'embeddings",
        message: "Nombre d'embeddings par lot. Recommandé: 50 pour éviter les timeouts.",
        position: "bottom"
      },
      {
        target: '[data-onboarding="system-embedding-btn"]',
        title: "8️⃣ Générer les embeddings",
        message: "Transforme le texte en vecteurs pour la recherche sémantique par l'IA.",
        position: "bottom"
      }
    ]
  }
];
